import "../home/home.css";
import "./about.css";
import { useTranslation } from "react-i18next";
import { useScrollReveal } from "../utils/useScrollReveal";

export default function About({ isLoggedIn, setLoginOpen, setAccountOpen, setPricingOpen, setFaqOpen }) {
  const { t, i18n } = useTranslation();

  // Scroll reveal hooks for different sections with lower threshold for smoother activation
  const heroSection = useScrollReveal({ threshold: 0.05 });
  const backedBySection = useScrollReveal({ threshold: 0.08 });
  const section1 = useScrollReveal({ threshold: 0.08 });
  const section2 = useScrollReveal({ threshold: 0.08 });
  const illustrationSection = useScrollReveal({ threshold: 0.08 });
  const teamSection = useScrollReveal({ threshold: 0.08 });

  const clickLogin = () => setLoginOpen(true);
  const changeLanguage = () => {
    if (i18n.language === "en") i18n.changeLanguage("fr");
    else i18n.changeLanguage("en");
  };

  return (
    <div className="about-page w-full flex flex-col items-center bg-[#f5f5f7] min-h-[100vh]">

      {/* Main Container */}
      <div className="w-full max-w-[1100px] px-6 py-12 md:py-16 space-y-6 md:space-y-12">

        {/* HERO SECTION */}
        <section 
          ref={heroSection.ref}
          className={`text-center space-y-3 scroll-reveal ${heroSection.isVisible ? 'visible' : ''}`}
        >
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900">
            {t("about.title", "About Us")}
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed">
            {t("about.subtitle", "Shaping the future of engineering standards with AI.")}
          </p>
        </section>

        {/* BACKED BY SECTION */}
        <section 
          ref={backedBySection.ref}
          className={`bg-white shadow-xl rounded-3xl p-8 md:p-12 transition-shadow duration-300 hover:shadow-2xl scroll-reveal scroll-reveal-delay-1 ${backedBySection.isVisible ? 'visible' : ''}`}
        >
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 bg-gradient-to-r from-[#56267B] to-[#20CADE] bg-clip-text text-transparent">
              Backed by
            </h2>
            <img
              src="/images/about_skydeck.png"
              alt="SkyDeck"
              className="h-10 md:h-12 object-contain"
            />
            <img
              src="/images/about_pad_13.png"
              alt="PAD 13"
              className="h-10 md:h-12 object-contain"
            />
          </div>
        </section>

        {/* SECTION 1: What We Believe In */}
        <section 
          ref={section1.ref}
          className={`bg-white shadow-xl rounded-3xl p-8 md:p-12 flex flex-col space-y-6 transition-shadow duration-300 hover:shadow-2xl scroll-reveal scroll-reveal-delay-1 ${section1.isVisible ? 'visible' : ''}`}
        >
          <div className="max-w-none">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 bg-gradient-to-r from-[#56267B] to-[#20CADE] bg-clip-text text-transparent">
              What we believe in
            </h2>
            <p className="text-[16px] md:text-[17px] text-gray-600 leading-relaxed">
              At <strong>CiviBOT</strong>, we believe in revolutionizing the civil engineering and construction industry through advanced AI technology. 
              Our mission is to simplify access to critical standards and regulations, ensuring safety, quality, and compliance are seamlessly integrated 
              into every project.
            </p>
          </div>
        </section>

        {/* SECTION 2: Why Our Solution is Needed */}
        <section 
          ref={section2.ref}
          className={`bg-white shadow-xl rounded-3xl p-8 md:p-12 transition-shadow duration-300 hover:shadow-2xl scroll-reveal scroll-reveal-delay-2 ${section2.isVisible ? 'visible' : ''}`}
        >
          <img
              src="/images/about_1.png"
              alt="Civil Engineering Challenges"
              className="w-full h-64 md:h-80 object-cover rounded-2xl shadow-md mb-8 hover:scale-[1.02] transition-transform duration-500"
          />
          
          <div className="space-y-4 text-left">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 bg-gradient-to-r from-[#3982B0] to-[#20CADE] bg-clip-text text-transparent">
              Why our solution is needed
            </h2>
            <div className="space-y-4 text-gray-700 text-[16px] md:text-[17px] leading-relaxed">
              <p>
                The civil engineering and construction industry is driven by stringent standards essential for ensuring safety, quality, and compliance. 
                Professionals in this field constantly refer to these standards at every step of their projects and often face the daunting task of sifting 
                through vast amounts of data, manuals, and extensive PDFs to locate specific standards.
              </p>
              <p>
                This traditional method is not only time-consuming but also prone to errors and omissions. As projects become more complex and timelines tighter, 
                the inefficiencies in accessing crucial standards can lead to delays, increased costs, and compromised quality.
              </p>
              <p>
                <strong>CiviBOT</strong> addresses this significant industry challenge by providing an AI-powered assistant that offers instant, accurate, and easily accessible information — 
                revolutionizing the way professionals interact with and utilize standards.
              </p>
            </div>
          </div>
        </section>

        {/* Existing SVG Illustration */}
        <section 
          ref={illustrationSection.ref}
          className={`w-full scroll-reveal-scale scroll-reveal-delay-3 ${illustrationSection.isVisible ? 'visible' : ''}`}
        >
          <img
            src="/images/about_civibot.svg"
            alt="CiviBOT Illustration"
            className="w-full rounded-xl hover:scale-[1.01] transition-transform duration-500"
          />
        </section>

        {/* TEAM SECTION */}
        <div 
          ref={teamSection.ref}
          className={`w-full scroll-reveal scroll-reveal-delay-4 ${teamSection.isVisible ? 'visible' : ''}`}
        >
          <svg className="w-full -mb-1" viewBox="0 0 1440 320">
            <path fill="#043b4a" fillOpacity="1" d="M0,224L1440,96L1440,320L0,320Z"></path>
          </svg>
          
          <section className="relative bg-[#043b4a] text-white rounded-b-3xl p-8 md:p-14 shadow-2xl overflow-hidden">
            <div className="flex flex-col gap-10">
              {/* First row: about_3 image with Mathieu & Ruben descriptions */}
              <div className="flex flex-col md:flex-row items-center gap-10">
                <div className="flex-1 flex justify-center md:justify-start">
                  <img
                    src="/images/about_3.png"
                    alt="Founders"
                    className="w-full max-w-md object-cover rounded-2xl shadow-2xl border border-[#20CADE]/20"
                  />
                </div>
                <div className="flex-1 space-y-6">
                  <h2 className="text-3xl md:text-[2.75rem] font-bold text-white mb-4">{t("about.teamTitle")}</h2>

                  <div className="space-y-5">
                    <div>
                      <p className="text-xl md:text-2xl font-bold text-white">
                        Mathieu <span className="font-normal text-white text-base md:text-lg">{t("about.teamCTO")}</span>
                      </p>
                      <p className="text-white text-justify leading-relaxed mt-2 text-base md:text-lg">{t("about.teamCTOdesc")}</p>
                    </div>

                    <div>
                      <p className="text-xl md:text-2xl font-bold text-white">
                        Ruben <span className="font-normal text-white text-base md:text-lg">{t("about.teamCEO")}</span>
                      </p>
                      <p className="text-white text-justify leading-relaxed mt-2 text-base md:text-lg">{t("about.teamCEOdesc")}</p>
                    </div>
                  </div>

                  <p className="text-white font-medium mt-6 text-justify leading-relaxed border-l-4 border-[#20CADE] pl-4 text-base md:text-lg">
                    {t("about.teamFinal")}
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-center gap-10">
                <div className="flex-1 flex justify-center">
                  <img
                    src="/images/about_Raphael.jpeg"
                    alt="Team Member"
                    className="w-full max-w-sm object-cover rounded-2xl shadow-2xl border border-[#20CADE]/20"
                  />
                </div>
                <div className="flex-1">
                  <div>
                    <p className="text-xl md:text-2xl font-bold text-white">
                      Raphael <span className="font-normal text-white text-base md:text-lg">({t("about.teamEn")})</span>
                    </p>
                    <p className="text-white text-justify leading-relaxed mt-2 text-base md:text-lg">{t("about.teamEndesc")}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="w-full mt-auto py-8 cursor-pointer flex flex-col items-center justify-center text-[14px]">
        <div className="flex flex-wrap items-center justify-center text-gray-500">
          {isLoggedIn ? (
            <a className="px-[6px] hover:text-[var(--blue-text)]" onClick={() => setAccountOpen(true)}>
              {t("home.footer_text1")}
            </a>
          ) : (
            <a className="px-[6px] hover:text-[var(--blue-text)]" onClick={clickLogin}>
              {t("home.footer_text2")}
            </a>
          )}
          <span className="text-gray-400 px-1">|</span>
          <a className="px-[6px] hover:text-[var(--blue-text)]" onClick={() => setFaqOpen(true)}>
            {t("home.faq")}
          </a>
          <span className="text-gray-400 px-1">|</span>
          <a
            className="px-[6px] hover:text-[var(--blue-text)]"
            onClick={() => window.open("https://www.linkedin.com/company/civibot")}
          >
            {t("home.footer_text4")}
          </a>
          <span className="text-gray-400 px-1">|</span>
          <a className="px-[6px] hover:text-[var(--blue-text)]" onClick={changeLanguage}>
            {t("selectLang")}
          </a>
        </div>
        <div className="footer-desc w-full text-center text-gray-500 text-[14px] mt-3">{t("footer")}</div>
      </footer>
    </div>
  );
}